"use strict";
import {Utils} from "@/backoffice/scripts/global.js";


if (window.registerModule) {
    registerModule('animations');
}

export class CanvasAnimation
{
    constructor(parent) {

        this.className = null;

        this.width = 0;
        this.height = 0;
        this.color = null;
        this.hardAssignedColor = false;

        this.element = document.createElement('canvas');
        this.element.classList.add('progress-indicator');

        /** @type {CanvasRenderingContext2D} */
        this.ctx = this.element.getContext('2d');

        this.lastTimestamp = performance.now();

        this.targetFPS = 1000 / 60; // 60fps;
        this.hasStarted = false;
        this.stopped = true;
        this.hiding = true;
        this.mode = 'indeterminate';

        this.progress = {
            main:   0,
            start:  0,
            end:    0,
            target: 0,
        };

        this.currentLineWidth = 0;

        this.tickSpeed = 1; // no touchy

        this.onHidden = null;

        if (parent) {
            this.append(parent);
        }

        new MutationObserver(() => {
            // style has changed, might as well check with setColor.
            if (! this.hardAssignedColor) { // but don't do it if there's a hard-assigned color set.
                this.setColor();
            }
        })
            .observe(this.element, {
                attributes: true,
            });

        if (! window.progressIndicators) {
            window.progressIndicators = [];
        }

        window.progressIndicators.push({
            indicator: this,
            parent,
        })
    }

    /**
     *
     * @param timestamp
     */
    render(timestamp) {
        if (this.stopped) {
            if (this.hiding) {
                // If it's stopped it means it's no longer animating 'out'.
                this.element.classList.replace('indicator-hiding', 'indicator-hidden');
                if (this.onHidden && this.hasStarted) {
                    this.onHidden.call(this);
                }
            }

            return false;
        }

        this.tickSpeed = (timestamp - this.lastTimestamp) / this.targetFPS;
        this.lastTimestamp = timestamp;

        return true;
    }

    /**
     * Appends the canvas to the given element.
     *
     * @param {HTMLElement} parent
     *
     * @return {CanvasAnimation}
     */
    append(parent) {
        parent.appendChild(this.element);

        return this;
    }

    /**
     *
     * @param {string} [color]
     *
     * @return {CanvasAnimation}
     */
    setColor(color) {
        if (color === 'inherit' || ! color) {
            // it means we need to look at the css coloring
            this.hardAssignedColor = false;

            // check the computedStyleMap, but only if there's support for it.
            // https://caniuse.com/mdn-api_element_computedstylemap
            if (this.element.computedStyleMap) {
                this.color = this.element.computedStyleMap().get('color');

                return this.start();
            }

            // This version of computedStyle is much more available, but a little slower.
            // https://caniuse.com/getcomputedstyle
            if (window.getComputedStyle) {
                this.color = getComputedStyle(this.element).getPropertyValue('color');

                return this.start();
            }

            // check the element style
            if (this.element.style.color) {
                this.color = this.element.style.color;

                return this.start();
            }
        }

        if (color === 'initial' || ! color) {
            // no color was found, or the default was requested. Apply the default.
            color = "#009EB4";
        }

        this.color = color;
        this.hardAssignedColor = true;

        return this.start();
    }

    /**
     * Sets the size of the element.
     *
     * @param {number} width - visual width
     * @param {number} [height] - override height
     *
     * @return {CanvasAnimation}
     */
    setSize(width, height)
    {
        return this;
    }

    /**
     * Sets the progress to animate towards. Can be between 0 and 1.
     *
     * @param {number} target
     *
     * @return {CanvasAnimation}
     */
    progressTo(target)
    {
        this.progress.target = target;

        return this.start();
    }

    /**
     * Sets the animation to run as a determinate progress indicator.
     *
     * Any current animation will smoothly transform to the determinate state.
     *
     * @return {CanvasAnimation}
     */
    determinate()
    {
        if (this.mode === 'determinate') {
            return this;
        }

        this.mode = 'determinate';

        return this.start();
    }

    /**
     * Sets the animation to run as a indeterminate progress indicator.
     *
     * @return {CanvasAnimation}
     */
    indeterminate()
    {
        this.mode = 'indeterminate';

        this.progress.main = 0;

        return this.start();
    }

    /**
     * Starts the animation (if it's stopped).
     *
     * @return {CanvasAnimation}
     */
    start()
    {
        if (this.stopped) {
            this.hasStarted = true;
            this.stopped = false;
            this.lastTimestamp = performance.now();

            window.requestAnimationFrame(this.render.bind(this));
        }

        return this;
    }

    /**
     * Stops the animation (if it's still running).
     *
     * @return {CanvasAnimation}
     */
    stop()
    {
        if (! this.stopped) {
            this.stopped = true;
        }

        return this;
    }

    /**
     * Smoothly hides the canvas from view. This is only visual, the element will still be in the DOM and it's display will remain 'visible'.
     *
     * @return {CanvasAnimation}
     */
    hide()
    {
        this.hiding = true;
        this.element.classList.add('indicator-hiding');

        return this.start();
    }

    /**
     * Animates the item back in.
     *
     * @return {CanvasAnimation}
     */
    show()
    {
        this.hiding = false;
        this.element.classList.remove('indicator-hidden', 'indicator-hiding');

        return this.start();
    }
}

// noinspection JSSuspiciousNameCombination
export class CircularIndicatorOld extends CanvasAnimation
{
    constructor(parent) {
        super(parent);

        this.className = 'circular-indicator';

        this.curvePoints = [0.0, 0.05, 0.95, 1.0];

        this.mainSpeed = 120; // 1 cycle per 120 frames.
        this.intSpeed = 85;   // 1 cycle per 85 frames.
        // If this is not a even fraction of mainSpeed, the animation will look less static (more chaotic, which looks better)

        this.element.classList.add(this.className);

        this.element.style.transform = "scale(0.5)";
        this.element.style.opacity = '0.0';

        // add an 'inter' key to the progress array.
        this.progress.inter = 0;

        this.setSize(48) // default size
            .setColor() // default color from CSS.
            .hide()
    }

    /**
     * Takes the given time and returns a value, based on the configured curvePoints.
     *
     * @param {number} t Time, between 0 and 1
     *
     * @return {number} The value at the given time, may exceed the 0-1 bounds based on the set curvePoints.
     */
    a(t) {
        return this.curvePoints[0] * Math.pow(1 - t, 3) +
               this.curvePoints[1] * 3 * Math.pow(1 - t, 2) * t +
               this.curvePoints[2] * 3 * (1 - t) * Math.pow(t, 2) +
               this.curvePoints[3] * Math.pow(t, 3);
    }

    render(timestamp) {
        if (! super.render(timestamp)) {
            return;
        }

        this.ctx.clearRect(0, 0, this.width, this.height);

        this.ctx.save();
        this.ctx.translate(this.width/2, this.height/2); // the circle center
        this.ctx.rotate(- Math.PI / 2); // rotate 1/4th anti-clockwise, so the circle starts at the top instead of the right.
        this.ctx.beginPath();

        // radius is center to edge, not diameter. That's why it's no more than 0.5.
        const radius = Math.min(this.width, this.height) * 0.4 - this.ctx.lineWidth / 2; // radius

        this.ctx.strokeStyle = this.color;

        if (this.mode === 'indeterminate') {
            if (this.currentLineWidth === 0) { // don't draw again if there's nothing to draw.
                this.stopped = true;
            }

            this.progress.main += this.tickSpeed / this.mainSpeed;
            this.progress.inter += this.tickSpeed / this.intSpeed;

            if (this.progress.inter > 1) { // always reset the progress ticker to be within the 0-1 range.
                this.progress.inter = this.progress.inter % 1;
            }

            let p = this.progress.inter * 2; // (value is 0-2)

            if (p < 1) { // first half of the animation (0-1)
                this.progress.start = p * 0.2; // linearly move the start from 0 to 20%

                this.progress.end = this.a(p) * 0.9 + 0.1; // animate the end from 10% to 100%
            } else { // second half.
                p -= 1; // subtract 1 from p as that still shows proper progress. (value is still 0-1)
                this.progress.end = 1.0 + p * 0.1; // linearly move the end from 100% to 110%

                this.progress.start = this.a(p) * 0.8 + 0.2; // animate the start from 20% to 100%
            }

            this.ctx.arc(
                0, // x | use translate above to move the center
                0, // y | use translate above to move the center
                radius,
                (this.progress.main + this.progress.start) * Math.PI * 2, // start
                (this.progress.main + this.progress.end) * Math.PI * 2 // end
            );

        } else {
            // animate to a given process %

            // stop on the next run, unless the following code says otherwise.
            this.stopped = true;

            this.ctx.arc(
                0, // x | use translate above to move the center
                0, // y | use translate above to move the center
                radius,
                this.progress.start * Math.PI * 2, // start
                this.progress.end * Math.PI * 2 // end
            );

            if (this.progress.end !== this.progress.target) {
                if (Math.abs(this.progress.target - this.progress.end) < 0.001) {
                    this.progress.end = this.progress.target;
                } else {
                    this.progress.end += (this.progress.target - this.progress.end) * 0.1;
                }

                // do not stop on the next draw so we can see the update.
                this.stopped = false;
            }

            if (this.progress.start !== 0) {
                if (Math.abs(0 - this.progress.start) < 0.001) {
                    this.progress.start = 0;
                } else {
                    this.progress.start += (0 - this.progress.start) * 0.1;
                }

                // do not stop on the next draw so we can see the update.
                this.stopped = false;
            }
        }

        this.ctx.stroke();
        this.ctx.restore();

        if (this.hiding && this.currentLineWidth !== 0) {
            if (this.currentLineWidth < 0.01) {
                this.currentLineWidth = 0;
            } else {
                this.currentLineWidth *= 0.9;
            }

            // do not stop on the next draw so we can see the update.
            this.stopped = false;
        }

        if (! this.hiding && this.currentLineWidth !== this.lineWidth) {
            if (Math.abs(this.lineWidth - this.currentLineWidth) < 0.01) {
                this.currentLineWidth = this.lineWidth;
            } else {
                this.currentLineWidth += (this.lineWidth - this.currentLineWidth) * 0.1;
            }

            // do not stop on the next draw so we can see the update.
            this.stopped = false;
        }

        this.ctx.lineWidth = this.currentLineWidth;

        window.requestAnimationFrame(this.render.bind(this));
    }

    /**
     * Sets the width (and height) of the animation canvas.
     * @param {number} width
     * @param {number} [height]
     * @return {CanvasAnimation}
     */
    setSize(width, height) {
        if (! height) {
            height = width;
        }

        this.height = height * 2;
        this.width = width * 2;

        this.element.width = this.width;
        this.element.height = this.height;

        // halve the visual width and height, while rendering at 1x. This increases the overall quality.
        this.element.style.width = (this.width / 2) + "px";
        this.element.style.height = (this.height / 2) + "px";

        this.lineWidth = Math.min(this.width, this.height) / 12;

        return this.start();
    }

    indeterminate() {
        this.progress.inter = 0;

        return super.indeterminate();
    }

    determinate() {
        if (this.mode === 'determinate') {
            return this;
        }

        this.progress.end = (this.progress.end + this.progress.main % 1) % 1 ;
        this.progress.start = (this.progress.start + this.progress.main % 1) % 1;
        if (this.progress.start > this.progress.end) {
            this.progress.end += 1;
        }

        return super.determinate();
    }

    show() {
        this.element.style.transition = "250ms transform cubic-bezier(0, 0, 0.2, 1), 150ms opacity linear";
        this.element.style.transform = "scale(1.0)";
        this.element.style.opacity = '1.0';

        return super.show();
    }

    hide() {
        this.element.style.transition = "250ms transform cubic-bezier(0.4, 0, 1, 1), 150ms opacity linear 50ms";
        this.element.style.transform = "scale(0.5)";
        this.element.style.opacity = '0.0';

        return super.hide();
    }

}

/**
 * Creates a new Linear Progress Indicator.
 */
export class LinearIndicatorOld extends CanvasAnimation
{
    constructor(parent) {
        super(parent);

        this.className = 'linear-indicator';

        this.mainSpeed = 120; // 1 full cycle per 120 frames at @targetFPS frames per second.

        /*this.currentLineWidth = 1; */// this ticker is only used to keep animating the effect while it's being hidden. Actual size is determined by the css animation.

        this.element.classList.add(this.className);

        this.element.style.transform = "scaleY(0.25)";
        this.element.style.opacity = '0.0';

        this.backgroundColor = Utils.isDarkMode() ? '#000000' : "#FFFFFF"

        this.setSize(240) // default size
            .setColor()  // default color fetched from CSS
        ;
    }

    render(timestamp) {
        if (! super.render(timestamp)) {
            return;
        }

        this.ctx.fillStyle = this.color;

        // fill in the entire thing with the background color (this overrides the previous colors)
        this.ctx.fillRect(0, 0, this.width, this.height);

        // overwrite the existing layer with a semi-transparent, white layer.
        this.ctx.save();
        this.ctx.fillStyle = this.backgroundColor;
        this.ctx.globalAlpha = 0.6;
        this.ctx.fillRect(0, 0, this.width, this.height);
        this.ctx.restore();

        switch (this.mode) {
            case 'indeterminate':
                if (this.currentLineWidth === 0) {
                    this.stopped = true;
                }

                this.progress.main += this.tickSpeed / this.mainSpeed;
                if (this.progress.main > 1) {
                    this.progress.main = this.progress.main % 1;
                }

                let p = this.progress.main * 2; // reaches 0-2.

                if (p < 1) { // stage 1, linearly move the end to the back.
                    if (p >= 1/3) {
                        const s = (p - 1/3) * (1/6*7);
                        this.progress.start = s * s; // the start slowly moves and speeds up, up to 66.7% of its animation
                    } else {
                        this.progress.start = 0;
                    }
                    this.progress.end = p * 1.2; // the end moves to the back quickly.

                } else { // stage 2
                    p -= 1

                    if (p < 1/3) { // finish the animation from the previous section.
                        const s = (p + 1/3*2) * (1/6*7);
                        this.progress.start = s * s;
                    } else { // catch up to the end again
                        const s = (p - 1/3) * (1/3*4);
                        this.progress.start = 1 - (1 - s) * (1 - s);
                    }

                    this.progress.end = Math.sin((p * Math.PI) / 2) * 1.15;
                }

                if (this.progress.end < this.progress.start) { // the start is ahead of the end. Draw 2 lines, one from start->100% and one from 0%->end.
                    this.ctx.fillRect(this.progress.start * this.width, 0, this.width, this.height);
                    this.ctx.fillRect(0, 0, this.progress.end * this.width, this.height);

                } else { // normal conditions
                    this.ctx.fillRect(this.progress.start * this.width, 0, (this.progress.end - this.progress.start) * this.width, this.height);
                }

                break;
            case 'determinate':
                // prepare to no longer draw the next frame, unless it's overwritten later.
                this.stopped = true;

                this.ctx.fillRect(this.progress.start, 0, this.progress.end * this.width, this.height);

                if (this.progress.end !== this.progress.target) { // move the end to the target.
                    if (Math.abs(this.progress.target - this.progress.end) < 0.005) {
                        this.progress.end = this.progress.target;
                    } else {
                        this.progress.end += (this.progress.target - this.progress.end) * 0.1 * this.tickSpeed;
                    }

                    this.stopped = false;
                }

                if (this.progress.start !== 0) { // move the start back to 0
                    if (this.progress.start < 0.01) {
                        this.progress.start = 0;
                    } else {
                        this.progress.start *= 0.9 * this.tickSpeed;
                    }

                    this.stopped = false;
                }

                break;
            default:
                console.warn(this.constructor.name,'unknown render mode:', this.mode);
                this.stopped = true;
        }

        if (this.currentLineWidth !== 1 && !this.hiding) {
            this.currentLineWidth = 1;

            this.stopped = false;

        } else if(this.currentLineWidth !== 0 && this.hiding) {
            if (this.currentLineWidth < 0.001) {
                this.currentLineWidth = 0;
            } else {
                this.currentLineWidth += (-this.currentLineWidth) * 0.1 * this.tickSpeed;
            }

            this.stopped = false;
        }

        // queue up the next frame
        window.requestAnimationFrame(this.render.bind(this));
    }

    /**
     * Sets the progress indicator's size.
     *
     * @param {number} width
     * @param {number} [height] Defaults to 4.
     *
     * @return {CanvasAnimation}
     */
    setSize(width, height)
    {
        this.width = width;
        this.height = height || 4;

        this.element.height = this.height;
        this.element.width = this.width;

        return this.start();
    }

    /**
     * Shows the element.
     *
     * @return {CanvasAnimation}
     */
    show()
    {
        this.element.style.transition = "250ms transform cubic-bezier(0, 0, 0.2, 1), 150ms opacity linear";
        this.element.style.transform = "scaleY(1.0)";
        this.element.style.opacity = '1.0';

        return super.show();
    }

    /**
     * Hides the element. Uses an animation. Does not remove the element from the DOM, instead, it fades it out.
     *
     * @return {CanvasAnimation}
     */
    hide()
    {
        this.element.style.transition = "250ms transform cubic-bezier(0.4, 0, 1, 1), 150ms opacity linear 50ms";
        this.element.style.transform = "scaleY(0.25)";
        this.element.style.opacity = '0.0';

        return super.hide();
    }
}


class ContainerTransformWrapper {
    /**
     *
     * @param {HTMLDivElement} parent
     */
    constructor(parent) {
        this.parent = parent;

        this.containers = [];

        this.parent.querySelectorAll('.container-transform')
            .forEach(container => {
                this.containers.push(new ContainerTransformer(container, this));
            });

        this.originalHeight = 0;
    }

    /**
     * Gets the size of the wrapper.
     * @return {DOMRect}
     */
    getSize() {
        return this.parent.getBoundingClientRect();
    }

    animateOpen(height) {
        this.originalHeight = this.getSize().height;

        this.parent.style.height = this.originalHeight + 'px';
        requestAnimationFrame(() => requestAnimationFrame(() => {
            this.parent.style.height = height + 'px';
        }));

        setTimeout(() => {
            this.parent.removeAttribute('style');
        }, 600);
    }

    animateClose(height) {
        this.parent.style.height = height + 'px';

        requestAnimationFrame(() => requestAnimationFrame(() => {
            this.parent.style.height = this.originalHeight + 'px';
        }));

        setTimeout(() => {
            this.parent.removeAttribute('style');
        }, 600);
    }

    disableAll(except) {
        this.containers.forEach(container => {
            if (container !== except) {
                container.container.style.display = 'none';
            }
        });
    }

    enableAll(except) {
        this.containers.forEach(container => {
            if (container !== except) {
                container.container.style.display = '';
            }
        })
    }
}

class ContainerTransformer {
    /**
     *
     * @param {HTMLElement} container
     * @param {ContainerTransformWrapper} wrapper
     */
    constructor(container, wrapper) {
        this.container = container;
        this.wrapper = wrapper;

        this.shape1 = container.querySelector('.transform-shape-1');
        this.shape2 = container.querySelector('.transform-shape-2');
        this.placebo = document.createElement('div');
        this.placebo.classList.add('CT-placebo');
        this.originalPosition = null;
        this.locked = false;
        this.state = 0;

        // find the triggers
        this.container.querySelectorAll('.transform-trigger')
            .on('click', this.toggle.bind(this));
    }

    spawnPlacebo(setSize = true) {
        if (setSize) {
            const position = this.getRelativePosition(this.shape1);
            this.placebo.style.width = position.width + 'px';
            this.placebo.style.height = position.height + 'px';

            this.originalPosition = position;
        }
        this.container.parentNode.insertBefore(this.placebo, this.container);
    }

    killPlacebo() {
        this.placebo.remove();
    }

    /**
     * @return {{top: number, left: number, width: number, height: number}}
     */
    getRelativePosition(source) {
        const pos1 = source.getBoundingClientRect();
        const pos2 = this.wrapper.parent.getBoundingClientRect();

        return {
            top: pos1.top - pos2.top,
            left: pos1.left - pos2.left,
            height: pos1.height,
            width: pos1.width,
        };
    }

    matchShape(size) {
        this.container.style.height = size.height + 'px';
        this.container.style.width = size.width + 'px';
        this.container.style.top = size.top + 'px';
        this.container.style.left = size.left + 'px';
    }

    toggle() {
        if (this.state === 0) {
            if (window.innerWidth < 768) { // The screen is too small to make it nice.
                const modal = new Modal({
                    content: this.shape2,
                });

                let triggered = false;
                this.shape2.on('click', 'a', () => {
                    if (! triggered) {
                        triggered = true;
                        modal.close();
                    }
                });

                modal.open();

                return;
            }
            this.open();
        } else {
            this.close();
        }
    }

    open() {
        if (this.locked) {
            return;
        }

        this.locked = true;
        this.spawnPlacebo();
        this.matchShape(this.originalPosition);
        this.container.classList.add('transforming');
        this.shape1.classList.add('hiding');
        this.shape2.classList.add('showing');

        const size = this.wrapper.getSize();
        this.shape2.firstElementChild.style.width = size.width + 'px';

        requestAnimationFrame(() => requestAnimationFrame(() => {
            const rect = this.shape2.getBoundingClientRect();
            this.container.style.width = '100%';
            this.container.style.height = rect.height + 'px';
            this.container.style.top = '0';
            this.container.style.left = '0';
            this.wrapper.animateOpen(rect.height);
        }));

        setTimeout(() => {
            this.killPlacebo();
            this.wrapper.disableAll(this);
            this.container.classList.remove('transforming');
            this.container.style.height = '';
            this.shape2.firstElementChild.style.width = '';
            this.state = 1;
            this.locked = false;
        }, 600);
    }

    close() {
        if (this.locked) {
            return;
        }

        this.locked = true;
        const size = this.getRelativePosition(this.shape2);
        this.matchShape(size);
        this.wrapper.enableAll(this);
        this.spawnPlacebo(false);
        this.container.classList.add('transforming');
        this.shape1.classList.remove('hiding');
        this.shape1.classList.add('showing');
        this.shape2.firstElementChild.style.width = size.width + 'px';
        this.shape2.classList.remove('showing');
        this.shape2.classList.add('hiding');

        requestAnimationFrame(() => requestAnimationFrame(() => {
            this.wrapper.animateClose(size.height);
            this.matchShape(this.originalPosition);
        }));

        setTimeout(() => {
            this.killPlacebo();
            this.container.removeAttribute('style');
            this.container.classList.remove('transforming');
            this.state = 0;
            this.locked = false;
            this.shape1.classList.remove('showing');
            this.shape2.classList.remove('hiding');
        }, 600);
    }

}

scope(() => {
    const parent = document.querySelector('.CT-block-wrapper');

    if (parent) {
        window.containerTransformWrapper = new ContainerTransformWrapper(parent);
    }
});

export class CircularIndicator extends CanvasAnimation {
    constructor(parent) {
        super(parent);

        this.className = 'hh-circular-indicator';

        this.mainSpeed = 120; // 1 full cycle per 60 frames at @targetFPS frames per second. (1 cycle per second)

        this.element.classList.add(this.className);
        this.element.style.transform = "scale(0.25)";
        this.element.style.opacity = '0.0';

        this.setSize(64) // default size
            .setColor()  // default color fetched from CSS
        ;
    }

    render(timestamp) {
        if (! super.render(timestamp)) {
            return;
        }

        if (this.mode === 'indeterminate') {
            this.progress.main += this.tickSpeed * 0.004;
            this.progress.main %= 1;
        } else if (this.progress.main !== this.progress.target) {
            if (Math.abs(this.progress.main - this.progress.target) < 0.1) {
                this.progress.main = this.progress.target;
            } else {
                this.progress.main += (this.progress.target - this.progress.main) / 8;
            }
        }

        this.ctx.clearRect(0, 0, this.width, this.height);
        this.ctx.textAlign = 'center';
        this.ctx.font = 'bold ' + (this.width * 0.4) + 'px Silka';
        this.ctx.fillStyle = this.color;

        this.ctx.save();

        this.ctx.translate(this.width/2, this.height/2);

        if (this.mode === 'indeterminate') {
            this.ctx.rotate(this.animate(this.progress.main) * Math.PI * 2);
            this.ctx.globalAlpha = (Math.sin(this.progress.main * Math.PI) + 1) * 0.5;
            const cos = 1 + (Math.sin(this.progress.main * Math.PI * 2) * 0.1 - 0.1);
            this.ctx.scale(cos, cos);
        }

        for (let i = 0; i < 8; i++) {
            if (this.mode === 'determinate') {
                if (this.progress.main > 1 / 8 * i) {
                    this.ctx.fillStyle = '#009EB4';
                } else {
                    this.ctx.fillStyle = '#ff8945';
                }
            }

            this.ctx.fillText('h', 0, this.width * 0.45);

            this.ctx.rotate(Math.PI / 4);
        }

        this.ctx.restore();

        // queue up the next frame
        window.requestAnimationFrame(this.render.bind(this));
    }

    animate(t) {
        return (t * 0.75 + this.ease(t) * 0.25) * 3;
    }

    ease(t) {
        return  t<.5 ? 2*t*t : -1+(4-2*t)*t;
    }

    /**
     * Sets the progress indicator's size.
     *
     * @param {number} width
     * @param {number} [height] Defaults to 4.
     *
     * @return {CanvasAnimation}
     */
    setSize(width, height)
    {
        this.width = width;
        this.height = height || width;

        this.element.height = this.height;
        this.element.width = this.width;

        return this.start();
    }

    /**
     * Shows the element.
     *
     * @return {CanvasAnimation}
     */
    show()
    {
        this.element.style.transition = "250ms transform cubic-bezier(0, 0, 0.2, 1), 150ms opacity linear";
        this.element.style.transform = "scale(1.0)";
        this.element.style.opacity = '1.0';

        return super.show();
    }

    /**
     * Hides the element. Uses an animation. Does not remove the element from the DOM, instead, it fades it out.
     *
     * @return {CanvasAnimation}
     */
    hide()
    {
        this.element.style.transition = "250ms transform cubic-bezier(0.4, 0, 1, 1), 150ms opacity linear 50ms";
        this.element.style.transform = "scale(0.25)";
        this.element.style.opacity = '0.0';

        return super.hide();
    }
}
window.CircularIndicator = CircularIndicator;

export class LinearIndicator extends CanvasAnimation {
    /**
     *
     * @param {HTMLElement} [parent]
     */
    constructor(parent) {
        super(parent);

        this.className = 'hh-linear-indicator';

        this.mainSpeed = 60; // 1 full cycle per 60 frames at @targetFPS frames per second. (1 cycle per second)

        this.element.classList.add(this.className);
        this.element.style.transform = "scale(0.25)";
        this.element.style.opacity = '0.0';

        this.setSize(200) // default size
            .setColor()  // default color fetched from CSS
        ;
    }


    render(timestamp) {
        if (! super.render(timestamp)) {
            return;
        }

        if (this.mode === 'indeterminate') {
            this.progress.main += this.tickSpeed * 0.01;
            this.progress.main %= 1;
        } else if (this.progress.main !== this.progress.target) {
            if (Math.abs(this.progress.main - this.progress.target) < 0.1) {
                this.progress.main = this.progress.target;
            } else {
                this.progress.main += (this.progress.target - this.progress.main) / 8;
            }
        }

        this.ctx.clearRect(0, 0, this.width, this.height);
        this.ctx.textAlign = 'center';
        this.ctx.textBaseline = 'middle';
        this.ctx.font = 'bold 20px Silka';
        this.ctx.fillStyle = this.color;

        this.ctx.save();

        const steps = 8;

        this.ctx.translate(this.width / (steps * 2), this.height / 2);

        for (let i = 0; i < steps; i++) {
            this.ctx.save();
            if (this.mode === 'indeterminate') {
                this.ctx.rotate(this.animate(Math.abs(1 + this.progress.main - i / steps * 0.5) % 1) * Math.PI * 2);
            } else {
                if (this.progress.main > 1 / steps * i) {
                    this.ctx.fillStyle = '#009EB4';
                } else {
                    this.ctx.fillStyle = '#ff8945';
                }
            }

            this.ctx.fillText('h', 0, 0);
            this.ctx.restore();
            this.ctx.translate(this.width / steps, 0);
        }



        this.ctx.restore();

        // queue up the next frame
        window.requestAnimationFrame(this.render.bind(this));
    }

    animate(t) {
        return t<.5 ? 4*t*t*t : (t-1)*(2*t-2)*(2*t-2)+1;
    }

    /**
     * Sets the progress indicator's size.
     *
     * @param {number} width
     * @param {number} [height] Defaults to 4.
     *
     * @return {CanvasAnimation}
     */
    setSize(width, height)
    {
        this.width = width;
        this.height = 20;

        this.element.height = this.height;
        this.element.width = this.width;

        return this.start();
    }

    /**
     * Shows the element.
     *
     * @return {CanvasAnimation}
     */
    show()
    {
        this.element.style.transition = "250ms transform cubic-bezier(0, 0, 0.2, 1), 150ms opacity linear";
        this.element.style.transform = "scaleY(1.0)";
        this.element.style.opacity = '1.0';

        return super.show();
    }

    /**
     * Hides the element. Uses an animation. Does not remove the element from the DOM, instead, it fades it out.
     *
     * @return {CanvasAnimation}
     */
    hide()
    {
        this.element.style.transition = "250ms transform cubic-bezier(0.4, 0, 1, 1), 150ms opacity linear 50ms";
        this.element.style.transform = "scaleY(0.25)";
        this.element.style.opacity = '0.0';

        return super.hide();
    }
}
window.LinearIndicator = LinearIndicator;
